import { useState, useEffect } from 'react';
import {
  Badge,
  Box,
  Button,
  Heading,
  HStack,
  Icon,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Text,
  VStack,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { FiArrowLeft, FiMail, FiSlash, FiCheck } from 'react-icons/fi';
import { Link, useParams } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { banUser, unbanUser } from '../../hooks/useAdmin';
import { useMyBounties } from '../../hooks/useBounties';
import { useMyClaims } from '../../hooks/useClaims';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { StatusBadge } from '../common/StatusBadge';
import { formatDate, formatRelativeTime } from '../../utils/formatters';
import { Price } from '../common/Price';

export default function AdminUserDetail() {
  const { userId } = useParams();
  const toast = useToast();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const { bounties } = useMyBounties(userId);
  const { claims } = useMyClaims(userId);

  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');

  useEffect(() => {
    if (!userId) return;
    const unsubscribe = onSnapshot(doc(db, 'users', userId), (snap) => {
      setUser(snap.exists() ? { id: snap.id, ...snap.data() } : null);
      setLoading(false);
    });
    return unsubscribe;
  }, [userId]);

  const handleToggleBan = async () => {
    try {
      if (user.isBanned) {
        await unbanUser(userId);
        toast({ title: 'User unbanned', status: 'success', duration: 3000, isClosable: true });
      } else {
        await banUser(userId);
        toast({ title: 'User banned', status: 'warning', duration: 3000, isClosable: true });
      }
    } catch (err) {
      toast({ title: 'Error', description: err.message, status: 'error', duration: 4000, isClosable: true });
    }
  };

  if (loading) return <LoadingSpinner message="Loading user..." />;

  if (!user) {
    return (
      <VStack spacing={4} py={8}>
        <Heading size="md">User not found</Heading>
        <Button as={Link} to="/admin/users" leftIcon={<Icon as={FiArrowLeft} />}>Back to Users</Button>
      </VStack>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      <Button
        as={Link}
        to="/admin/users"
        variant="ghost"
        leftIcon={<Icon as={FiArrowLeft} />}
        alignSelf="flex-start"
        size="sm"
      >
        Back to Users
      </Button>

      {/* User Info */}
      <Box bg={bg} rounded="xl" borderWidth={1} borderColor={borderColor} p={6}>
        <VStack align="stretch" spacing={4}>
          <HStack justify="space-between" align="start">
            <Box>
              <Heading size="md">{user.anonymizedUsername || 'No username'}</Heading>
              <Text color="gray.500" mt={1}>{user.email}</Text>
            </Box>
            <HStack spacing={2}>
              {user.isAdmin && <Badge colorScheme="orange" fontSize="sm">Admin</Badge>}
              {user.isBanned && <Badge colorScheme="red" fontSize="sm">Banned</Badge>}
            </HStack>
          </HStack>

          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
            <Stat>
              <StatLabel>Joined</StatLabel>
              <StatNumber fontSize="md">{formatDate(user.createdAt)}</StatNumber>
            </Stat>
            <Stat>
              <StatLabel>Rating</StatLabel>
              <StatNumber fontSize="md">
                {user.reputation?.rating ? `${user.reputation.rating.toFixed(1)} / 5` : 'N/A'}
              </StatNumber>
            </Stat>
            <Stat>
              <StatLabel>Completed Tasks</StatLabel>
              <StatNumber fontSize="md">{user.reputation?.completedBounties || 0}</StatNumber>
            </Stat>
            <Stat>
              <StatLabel>Total Ratings</StatLabel>
              <StatNumber fontSize="md">{user.reputation?.totalRatings || 0}</StatNumber>
            </Stat>
          </SimpleGrid>

          <HStack spacing={2} pt={2} flexWrap="wrap">
            <Button
              size="sm"
              colorScheme={user.isBanned ? 'green' : 'red'}
              variant="outline"
              leftIcon={<Icon as={user.isBanned ? FiCheck : FiSlash} />}
              onClick={handleToggleBan}
            >
              {user.isBanned ? 'Unban User' : 'Ban User'}
            </Button>
            <Button
              as={Link}
              to={`/admin/email?userId=${userId}&username=${encodeURIComponent(user.anonymizedUsername || '')}`}
              size="sm"
              variant="outline"
              leftIcon={<Icon as={FiMail} />}
            >
              Send Email
            </Button>
          </HStack>
        </VStack>
      </Box>

      {/* User's Bounties */}
      <Box bg={bg} rounded="xl" borderWidth={1} borderColor={borderColor} p={6}>
        <Heading size="sm" mb={4}>Tasks ({bounties.length})</Heading>
        {bounties.length === 0 ? (
          <Text color="gray.500" fontSize="sm">No tasks</Text>
        ) : (
          <VStack align="stretch" spacing={2}>
            {bounties.slice(0, 10).map((b) => (
              <HStack key={b.id} justify="space-between" py={1}>
                <Text fontSize="sm" noOfLines={1} flex={1}>{b.title}</Text>
                <HStack spacing={2}>
                  <Text fontSize="xs" color="gray.500"><Price cents={b.rewardAmount} size="sm" /></Text>
                  <StatusBadge status={b.status} />
                </HStack>
              </HStack>
            ))}
            {bounties.length > 10 && (
              <Text fontSize="xs" color="gray.400">...and {bounties.length - 10} more</Text>
            )}
          </VStack>
        )}
      </Box>

      {/* User's Claims */}
      <Box bg={bg} rounded="xl" borderWidth={1} borderColor={borderColor} p={6}>
        <Heading size="sm" mb={4}>Submissions ({claims.length})</Heading>
        {claims.length === 0 ? (
          <Text color="gray.500" fontSize="sm">No submissions</Text>
        ) : (
          <VStack align="stretch" spacing={2}>
            {claims.slice(0, 10).map((c) => (
              <HStack key={c.id} justify="space-between" py={1}>
                <Text fontSize="sm" noOfLines={1} flex={1}>{c.bountyTitle}</Text>
                <HStack spacing={2}>
                  <Text fontSize="xs" color="gray.500">{formatRelativeTime(c.submittedAt)}</Text>
                  <StatusBadge status={c.status} />
                </HStack>
              </HStack>
            ))}
            {claims.length > 10 && (
              <Text fontSize="xs" color="gray.400">...and {claims.length - 10} more</Text>
            )}
          </VStack>
        )}
      </Box>

    </VStack>
  );
}
